import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Container, Card, Row, Col, Table, Badge, Spinner, Form, Button, Alert, ProgressBar } from 'react-bootstrap';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { BarChart, PieChart, FunnelChart } from 'echarts/charts';
import { GridComponent, TooltipComponent, LegendComponent, TitleComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { toast } from 'react-toastify';
import budgetService from '../../../services/budgetService';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Select from 'react-select';
import { useAuth } from '../../../contexts/AuthContext';

echarts.use([BarChart, PieChart, FunnelChart, GridComponent, TooltipComponent, LegendComponent, TitleComponent, CanvasRenderer]);

const KPICard = ({ title, value, subtitle, color = '#2c7be5' }) => (
  <Card className="h-100 border-0 shadow-sm text-center" style={{ borderTop: `3px solid ${color}` }}>
    <Card.Body className="py-3">
      <p className="text-muted small mb-1 text-uppercase">{title}</p>
      <h3 className="mb-0 fw-bold" style={{ color }}>{value}</h3>
      {subtitle && <small className="text-muted">{subtitle}</small>}
    </Card.Body>
  </Card>
);

const ClaroWhatsappDashboard = () => {
  const { hasRole, user } = useAuth();
  const canUpload = hasRole('admin') || hasRole('super_admin') || hasRole('ADMIN') || hasRole('SUPER_ADMIN') || hasRole('MANAGER') || user?.isSuperAdmin;
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setDate(1); return d;
  });
  const [dateTo, setDateTo] = useState(new Date());
  const [selectedVertical, setSelectedVertical] = useState([]);
  const [selectedContactado, setSelectedContactado] = useState([]);
  const [selectedRespuesta, setSelectedRespuesta] = useState([]);

  const [isSearching, setIsSearching] = useState(false);

  const loadData = useCallback(async (showSpinner = true) => {
    if (showSpinner) setIsSearching(true);
    else setLoading(true);
    try {
      const result = await budgetService.getWhatsappDashboard({
        dateFrom: dateFrom.toISOString().split('T')[0],
        dateTo: dateTo.toISOString().split('T')[0],
        productType: selectedVertical.length > 0 ? selectedVertical.join(',') : undefined,
        contactado: selectedContactado.length > 0 ? selectedContactado.join(',') : undefined,
        respuesta: selectedRespuesta.length > 0 ? selectedRespuesta.join(',') : undefined,
      });
      if (result.success) setData(result.data);
      else toast.error(result.error);
    } catch (e) {
      toast.error('Error cargando datos WhatsApp');
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  }, [dateFrom, dateTo, selectedVertical, selectedContactado, selectedRespuesta]); // eslint-disable-line

  // Solo cargar al montar (inicial)
  useEffect(() => { loadData(false); }, []); // eslint-disable-line

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().match(/\.(xlsx|xlsb)$/)) {
      toast.error('Solo archivos .xlsx o .xlsb');
      return;
    }

    setUploading(true);
    setUploadProgress({ progress: 2, message: 'Enviando archivo al servidor...', status: 'uploading' });

    try {
      const fd = new FormData();
      fd.append('file', file);
      const result = await budgetService.uploadWhatsappFile(fd);

      if (result.success && result.data?.jobId) {
        const jobId = result.data.jobId;
        setUploadProgress({ progress: 10, message: 'Procesando archivo...', status: 'parsing' });

        const poll = async () => {
          for (let attempt = 0; attempt < 300; attempt++) {
            await new Promise((res) => setTimeout(res, 2000));
            const statusR = await budgetService.getWhatsappUploadStatus(jobId);
            if (statusR.success && statusR.data) {
              const st = statusR.data;
              setUploadProgress({ progress: st.progress, message: st.message, status: st.status });

              if (st.status === 'completed') {
                const s = st.stats || {};
                toast.success(
                  st.message ||
                    `Cargados ${(s.totalRegistros || 0).toLocaleString()} registros (${(s.totalPauta || 0).toLocaleString()} PAUTA)`,
                  { autoClose: 6000 },
                );
                setUploadProgress(null);
                setUploading(false);
                await loadData(false);
                return;
              }
              if (st.status === 'error') {
                toast.error(st.error || 'Error al procesar archivo');
                setUploadProgress(null);
                setUploading(false);
                return;
              }
            }
          }
          toast.warning('El proceso sigue en segundo plano. Recarga la página para ver los resultados.');
          setUploadProgress(null);
          setUploading(false);
        };
        poll();
      } else if (result.success && result.data?.stats) {
        const s = result.data.stats;
        toast.success(
          `Cargados ${s.totalRegistros.toLocaleString()} registros (${s.totalPauta} PAUTA)`,
          { autoClose: 6000 },
        );
        setUploadProgress(null);
        setUploading(false);
        loadData(false);
      } else {
        toast.error(result.error || 'Error al subir archivo');
        setUploadProgress(null);
        setUploading(false);
      }
    } catch {
      toast.error('Error subiendo archivo');
      setUploadProgress(null);
      setUploading(false);
    } finally {
      e.target.value = '';
    }
  };

  const kpis = data?.kpis || {};
  const fmt = (v) => parseInt(v || 0).toLocaleString();
  const pct = (v) => ((parseFloat(v || 0)) * 100).toFixed(1) + '%';

  const funnelOption = useMemo(() => {
    if (!kpis.totalConversaciones) return null;
    const total = parseInt(kpis.totalConversaciones) || 0;
    const contactados = parseInt(kpis.totalContactados) || 0;
    const gestionFin = parseInt(kpis.totalGestionFinalizada) || 0;
    const acepta = parseInt(kpis.totalAcepta) || 0;
    const steps = [
      { name: 'Conversaciones', value: total, itemStyle: { color: '#B8D4E3' } },
      { name: 'Contactados', value: contactados, itemStyle: { color: '#6BAED6' } },
      { name: 'Gestión Finalizada', value: gestionFin, itemStyle: { color: '#2171B5' } },
      { name: 'Acepta', value: acepta, itemStyle: { color: '#25d366' } },
    ];
    return {
      tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
      series: [{
        type: 'funnel', left: '10%', width: '80%', top: 10, bottom: 10,
        minSize: '15%', maxSize: '100%',
        sort: 'descending', gap: 4,
        label: { show: true, position: 'inside', formatter: '{b}\n{c}', fontSize: 12, color: '#fff' },
        data: steps,
      }],
    };
  }, [kpis]);

  const byRespPieOption = useMemo(() => {
    const byResp = (data?.byRespuesta || []).filter(r => r.total > 0).slice(0, 10);
    if (!byResp.length) return null;
    return {
      tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
      series: [{
        type: 'pie', radius: ['40%', '70%'], center: ['50%', '50%'],
        label: { show: true, fontSize: 10, formatter: '{b}: {c}' },
        data: byResp.map(r => ({ name: r.respuesta, value: r.total })),
      }],
    };
  }, [data]);

  if (loading) {
    return (
      <Container fluid className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 text-muted">Cargando datos WhatsApp...</p>
      </Container>
    );
  }

  return (
    <Container fluid className="py-3" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <Card className="mb-3 shadow-sm border-0">
        <Card.Body className="py-3">
          <Row className="g-2 align-items-end">
            <Col xs={12} md={3}>
              <h5 className="mb-0 fw-bold" style={{ color: '#1B2A4A' }}>
                <span className="me-2" style={{ color: '#25d366' }}>&#9679;</span>
                WhatsApp SAC — Claro
              </h5>
              <small className="text-muted">Consolidado de conversaciones de pauta</small>
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="small text-muted mb-1">Desde</Form.Label>
              <DatePicker selected={dateFrom} onChange={setDateFrom} dateFormat="dd/MM/yyyy"
                className="form-control form-control-sm" />
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="small text-muted mb-1">Hasta</Form.Label>
              <DatePicker selected={dateTo} onChange={setDateTo} dateFormat="dd/MM/yyyy"
                className="form-control form-control-sm" />
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="small text-muted mb-1">Vertical</Form.Label>
              <Select
                isMulti isClearable isSearchable
                value={selectedVertical.map(v => ({ value: v, label: v }))}
                onChange={(opts) => setSelectedVertical((opts || []).map(o => o.value))}
                options={[
                  { value: 'EQUIPOS', label: 'Equipos' },
                  { value: 'PREPAGO', label: 'Prepago' },
                  { value: 'POSTPAGO', label: 'Postpago' },
                  { value: 'HOGAR', label: 'Hogar' },
                  { value: 'CORPORATIVO', label: 'Corporativo' },
                ]}
                placeholder="Todas"
                styles={{ control: (b) => ({ ...b, minHeight: 31, fontSize: 12 }), valueContainer: (b) => ({ ...b, padding: '0 6px' }) }}
              />
            </Col>
            {canUpload && (
              <Col xs={6} md={3}>
                <Form.Label className="small text-muted mb-1">Cargar archivo</Form.Label>
                <Form.Control type="file" size="sm" accept=".xlsx,.xlsb" onChange={handleUpload} disabled={uploading} />
              </Col>
            )}
          </Row>
          <Row className="g-2 mt-2">
            <Col xs={6} md={2}>
              <Form.Label className="small text-muted mb-1">Contactado</Form.Label>
              <Select
                isMulti isClearable
                value={selectedContactado.map(v => ({ value: v, label: v }))}
                onChange={(opts) => setSelectedContactado((opts || []).map(o => o.value))}
                options={[
                  { value: 'Contactados', label: 'Contactados' },
                  { value: 'No contactados', label: 'No contactados' },
                ]}
                placeholder="Todos"
                styles={{ control: (b) => ({ ...b, minHeight: 31, fontSize: 12 }), valueContainer: (b) => ({ ...b, padding: '0 6px' }) }}
              />
            </Col>
            <Col xs={6} md={4}>
              <Form.Label className="small text-muted mb-1">Respuesta</Form.Label>
              <Select
                isMulti isClearable isSearchable
                value={selectedRespuesta.map(v => ({ value: v, label: v }))}
                onChange={(opts) => setSelectedRespuesta((opts || []).map(o => o.value))}
                options={[
                  { value: 'ACEPTA', label: 'Acepta (todas)' },
                  { value: 'ACEPTA - EQUIPOS', label: 'Acepta - Equipos' },
                  { value: 'ACEPTA - PREPAGO', label: 'Acepta - Prepago' },
                  { value: 'ACEPTA - MIGRACIÓN', label: 'Acepta - Migración' },
                  { value: 'ACEPTA - HOGAR', label: 'Acepta - Hogar' },
                  { value: 'ACEPTA - CLARO PAY', label: 'Acepta - Claro Pay' },
                  { value: 'ACEPTA - PORTABILIDAD', label: 'Acepta - Portabilidad' },
                  { value: 'ACEPTA - LÍNEA NUEVA', label: 'Acepta - Línea Nueva' },
                  { value: 'ACEPTA - OTROS PRODUCTOS', label: 'Acepta - Otros Prod.' },
                  { value: 'GESTION FINALIZADA', label: 'Gestión Finalizada (todas)' },
                  { value: 'INACTIVIDAD', label: 'Inactividad' },
                  { value: 'TRANSFERENCIA', label: 'Transferencia' },
                  { value: 'Finalizado por bot', label: 'Finalizado por bot' },
                ]}
                placeholder="Todas"
                styles={{ control: (b) => ({ ...b, minHeight: 31, fontSize: 12 }), valueContainer: (b) => ({ ...b, padding: '0 6px' }) }}
              />
            </Col>
            <Col xs={12} md={2} className="d-flex align-items-end gap-2">
              <Button
                variant="primary"
                size="sm"
                className="fw-semibold px-3"
                onClick={() => loadData(true)}
                disabled={isSearching}
                style={{ minHeight: 31 }}
              >
                {isSearching ? <><Spinner animation="border" size="sm" className="me-1" /> Buscando...</> : 'Buscar'}
              </Button>
              {(selectedVertical.length > 0 || selectedContactado.length > 0 || selectedRespuesta.length > 0) && (
                <Button variant="outline-danger" size="sm" style={{ minHeight: 31 }} onClick={() => {
                  setSelectedVertical([]); setSelectedContactado([]); setSelectedRespuesta([]);
                  setTimeout(() => loadData(true), 50);
                }}>
                  Limpiar
                </Button>
              )}
            </Col>
          </Row>
          {data?.uploadInfo && (
            <div className="mt-2">
              <Badge bg="light" text="dark" className="border" style={{ fontSize: 10 }}>
                Último upload: {data.uploadInfo.fileName} — {new Date(data.uploadInfo.fechaCarga).toLocaleDateString()}
              </Badge>
            </div>
          )}
        </Card.Body>
      </Card>

      {uploadProgress && (
        <Alert variant="success" className="py-2 mb-3">
          <div className="d-flex align-items-center gap-2">
            <Spinner animation="border" size="sm" variant="success" />
            <div className="flex-grow-1">
              <div className="d-flex justify-content-between mb-1">
                <small className="fw-bold">{uploadProgress.message}</small>
                <small>{uploadProgress.progress}%</small>
              </div>
              <ProgressBar
                now={uploadProgress.progress}
                animated
                striped
                variant="success"
                style={{ height: 8 }}
              />
            </div>
          </div>
        </Alert>
      )}

      {/* Overlay sutil al buscar */}
      {isSearching && (
        <div className="position-fixed top-0 start-0 w-100" style={{ zIndex: 9998 }}>
          <div style={{ height: 3, background: 'linear-gradient(90deg, #25d366, #2c7be5, #25d366)', backgroundSize: '200% 100%', animation: 'searchBar 1.5s infinite' }} />
          <style>{`@keyframes searchBar { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>
        </div>
      )}

      {/* Filtros activos */}
      {(selectedVertical.length > 0 || selectedContactado.length > 0 || selectedRespuesta.length > 0) && (
        <div className="mb-2 d-flex align-items-center gap-2 flex-wrap">
          <small className="text-muted fw-semibold">Filtros activos:</small>
          {selectedVertical.map(v => (
            <Badge key={v} bg="primary" style={{ cursor: 'pointer', fontSize: 11 }}
              onClick={() => setSelectedVertical(prev => prev.filter(x => x !== v))}>
              {v} ✕
            </Badge>
          ))}
          {selectedContactado.map(v => (
            <Badge key={v} bg="info" style={{ cursor: 'pointer', fontSize: 11 }}
              onClick={() => setSelectedContactado(prev => prev.filter(x => x !== v))}>
              {v} ✕
            </Badge>
          ))}
          {selectedRespuesta.map(v => (
            <Badge key={v} bg="warning" text="dark" style={{ cursor: 'pointer', fontSize: 11 }}
              onClick={() => setSelectedRespuesta(prev => prev.filter(x => x !== v))}>
              {v} ✕
            </Badge>
          ))}
          <Button variant="link" size="sm" className="text-danger p-0 ms-2" style={{ fontSize: 11 }}
            onClick={() => { setSelectedVertical([]); setSelectedContactado([]); setSelectedRespuesta([]); }}>
            Limpiar todos
          </Button>
        </div>
      )}

      {/* KPI Cards */}
      <Row className="g-2 mb-3">
        <Col xs={6} md>
          <KPICard title="Conversaciones" value={fmt(kpis.totalConversaciones)} subtitle="Solo PAUTA" color="#25d366" />
        </Col>
        <Col xs={6} md>
          <KPICard title="Contactados" value={fmt(kpis.totalContactados)} subtitle={`Tasa: ${pct(kpis.tasaContacto)}`} color="#2c7be5" />
        </Col>
        <Col xs={6} md>
          <KPICard title="No Contactados" value={fmt(kpis.totalNoContactados)} color="#ffc107" />
        </Col>
        <Col xs={6} md>
          <KPICard title="Acepta" value={fmt(kpis.totalAcepta)} subtitle={`Tasa: ${pct(kpis.tasaAceptacion)}`} color="#28a745" />
        </Col>
        <Col xs={6} md>
          <KPICard title="Gestión Finalizada" value={fmt(kpis.totalGestionFinalizada)} color="#6c757d" />
        </Col>
        <Col xs={6} md>
          <KPICard title="Inactividad" value={fmt(kpis.totalInactividad)} color="#dc3545" />
        </Col>
      </Row>

      <Row className="g-3 mb-3">
        {/* Funnel */}
        <Col xs={12} md={6}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Header className="bg-white border-bottom py-2">
              <h6 className="mb-0 fw-bold" style={{ fontSize: 13, color: '#1B2A4A' }}>Embudo de Conversión</h6>
            </Card.Header>
            <Card.Body>
              {funnelOption ? (
                <ReactEChartsCore echarts={echarts} option={funnelOption} style={{ height: 300 }} />
              ) : (
                <div className="text-center text-muted py-5">Sin datos para el embudo</div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Pie de respuestas */}
        <Col xs={12} md={6}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Header className="bg-white border-bottom py-2">
              <h6 className="mb-0 fw-bold" style={{ fontSize: 13, color: '#1B2A4A' }}>Distribución por Respuesta</h6>
            </Card.Header>
            <Card.Body>
              {byRespPieOption ? (
                <ReactEChartsCore
                  echarts={echarts}
                  option={byRespPieOption}
                  style={{ height: 300 }}
                  onEvents={{
                    click: (params) => {
                      if (params.name) {
                        const newVal = selectedRespuesta.includes(params.name)
                          ? selectedRespuesta.filter(x => x !== params.name) : [...selectedRespuesta, params.name];
                        setSelectedRespuesta(newVal);
                        setTimeout(() => loadData(true), 50);
                      }
                    }
                  }}
                />
              ) : (
                <div className="text-center text-muted py-5">Sin datos</div>
              )}
              {selectedRespuesta.length > 0 && (
                <div className="text-center mt-1">
                  {selectedRespuesta.map(r => (
                    <Badge key={r} bg="info" className="me-1" style={{ fontSize: 10 }}>
                      {r} <span style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); setSelectedRespuesta(prev => prev.filter(x => x !== r)); }}>✕</span>
                    </Badge>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Por Vertical */}
      <Row className="g-3 mb-3">
        <Col xs={12} md={6}>
          <Card className="shadow-sm border-0">
            <Card.Header className="bg-white border-bottom py-2">
              <h6 className="mb-0 fw-bold" style={{ fontSize: 13, color: '#1B2A4A' }}>Por Vertical (Producto)</h6>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
              <Table hover size="sm" className="mb-0" style={{ fontSize: 11 }}>
                <thead className="bg-light">
                  <tr>
                    <th>Vertical</th>
                    <th className="text-end">Conv.</th>
                    <th className="text-end">Contactados</th>
                    <th className="text-end">No Contact.</th>
                    <th className="text-end text-success">Acepta</th>
                    <th className="text-end" style={{ color: '#fd7e14' }}>Acepta Otros</th>
                    <th className="text-end">Gestión Fin.</th>
                    <th className="text-end">Inactividad</th>
                    <th className="text-end">Transfer.</th>
                    <th className="text-end">Tasa Acept.</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.byVertical || []).map((v, i) => (
                    <tr key={i} style={{ cursor: 'pointer' }}
                      onClick={() => {
                        const newVal = selectedVertical.includes(v.vertical)
                          ? selectedVertical.filter(x => x !== v.vertical) : [...selectedVertical, v.vertical];
                        setSelectedVertical(newVal);
                        setTimeout(() => loadData(true), 50);
                      }}
                      className={selectedVertical.includes(v.vertical) ? 'table-primary' : ''}
                    >
                      <td>
                        <Badge bg={selectedVertical.includes(v.vertical) ? 'success' : 'primary'} style={{ fontSize: 10 }}>{v.vertical}</Badge>
                      </td>
                      <td className="text-end">{fmt(v.totalConversaciones)}</td>
                      <td className="text-end">{fmt(v.totalContactados)}</td>
                      <td className="text-end text-muted">{fmt(v.totalNoContactados)}</td>
                      <td className="text-end fw-bold text-success">{fmt(v.totalAcepta)}</td>
                      <td className="text-end" style={{ color: '#fd7e14' }}>{fmt(v.totalAceptaOtros)}</td>
                      <td className="text-end">{fmt(v.totalGestionFinalizada)}</td>
                      <td className="text-end text-danger">{fmt(v.totalInactividad)}</td>
                      <td className="text-end text-muted">{fmt(v.totalTransferencia)}</td>
                      <td className="text-end fw-bold">
                        {parseInt(v.totalContactados) > 0
                          ? ((parseInt(v.totalAcepta) / parseInt(v.totalContactados)) * 100).toFixed(1) + '%'
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-light fw-bold">
                  <tr>
                    <td>Total</td>
                    {['totalConversaciones','totalContactados','totalNoContactados','totalAcepta','totalAceptaOtros','totalGestionFinalizada','totalInactividad','totalTransferencia'].map(field => (
                      <td key={field} className="text-end">
                        {fmt((data?.byVertical || []).reduce((s, v) => s + (parseInt(v[field]) || 0), 0))}
                      </td>
                    ))}
                    <td className="text-end">
                      {(() => {
                        const rows = data?.byVertical || [];
                        const cont = rows.reduce((s, v) => s + (parseInt(v.totalContactados) || 0), 0);
                        const acep = rows.reduce((s, v) => s + (parseInt(v.totalAcepta) || 0), 0);
                        return cont > 0 ? (acep / cont * 100).toFixed(1) + '%' : '—';
                      })()}
                    </td>
                  </tr>
                </tfoot>
              </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Por Campaña */}
        <Col xs={12} md={6}>
          <Card className="shadow-sm border-0">
            <Card.Header className="bg-white border-bottom py-2">
              <h6 className="mb-0 fw-bold" style={{ fontSize: 13, color: '#1B2A4A' }}>Top Campañas de Pauta</h6>
            </Card.Header>
            <Card.Body className="p-0" style={{ maxHeight: 350, overflowY: 'auto' }}>
              <Table hover size="sm" className="mb-0" style={{ fontSize: 12 }}>
                <thead className="bg-light sticky-top">
                  <tr>
                    <th>Campaña</th>
                    <th>Vertical</th>
                    <th className="text-end">Conv.</th>
                    <th className="text-end">Contact.</th>
                    <th className="text-end">Acepta</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.byCampana || []).map((c, i) => (
                    <tr key={i}>
                      <td className="text-truncate" style={{ maxWidth: 160 }} title={c.campana}>{c.campana}</td>
                      <td><Badge bg="secondary" style={{ fontSize: 9 }}>{c.vertical || '—'}</Badge></td>
                      <td className="text-end">{fmt(c.totalConversaciones)}</td>
                      <td className="text-end">{fmt(c.totalContactados)}</td>
                      <td className="text-end fw-bold text-success">{fmt(c.totalAcepta)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <div className="text-center text-muted small mt-3">
        {data?.dateRange && (
          <span>Datos: {data.dateRange.from} a {data.dateRange.to}</span>
        )}
        {data?.meta?.queryTimeMs && (
          <span className="ms-2">| Procesado en {data.meta.queryTimeMs}ms</span>
        )}
      </div>
    </Container>
  );
};

export default ClaroWhatsappDashboard;
